/* eslint-disable no-console */
import { useFormik } from 'formik';
import { useCallback } from 'react';
import { useMutation } from 'react-query';

import { Button, InputField, Dropdown, Card, Form, Loader } from '@/components';
import { AnimatePresenceComponent } from '@/components/AnimatePresenceComponent';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import useToast from '@/hooks/useToast';
import ROUTES from '@/router/routes';
import { postRecoverEmail } from '@/services';
import {
  DataPostRecoverEmail,
  ResponseRecoverEmail,
  SelectValue,
} from '@/types';
import { DOC_TYPES, recoverEmailSchema, validateFieldError } from '@/utils';

const initialValues: DataPostRecoverEmail = {
  selectedTypeDoc: DOC_TYPES[0],
  docNumber: '',
};

interface Props {
  setDataResponse: (r: ResponseRecoverEmail, d: DataPostRecoverEmail) => void;
}

let dataForm: DataPostRecoverEmail;

export const RecoverEmail = ({ setDataResponse }: Props) => {
  const toast = useToast();
  const { isMobile } = useIsDesktop();

  const {
    mutate: postData,
    isLoading,
    isSuccess,
  } = useMutation(
    async (formData: DataPostRecoverEmail) => {
      dataForm = formData;
      return await postRecoverEmail(1, formData);
    },
    {
      onSuccess: (res) => {
        if (res.estado) {
          setDataResponse(res, dataForm);
        } else {
          formik.resetForm({
            values: {
              docNumber: formik.values.docNumber,
              selectedTypeDoc: formik.values.selectedTypeDoc,
            },
          });
          formik.setFieldError('errorMsg', res.mensaje);
        }
      },
      onError: (err) => {
        const error = err as Error;
        toast.show(error.message);
      },
    }
  );

  const formik = useFormik({
    initialValues,
    validationSchema: recoverEmailSchema,
    onSubmit: (val: DataPostRecoverEmail, { setSubmitting }) => {
      postData(val);
      setSubmitting(false);
    },
  });

  const handleSelect = useCallback(
    (e: SelectValue) => {
      formik.setFieldValue('selectedTypeDoc', e);
    },
    [formik]
  );

  const mainContent = useCallback(
    () => (
      <div>
        <p className="text-sm font-medium mb-4 lg:mb-6 lg:mt-5">
          Si no te acordás con qué correo electrónico jugaste a Gran DT, ingresá
          tus datos para consultarlo.
        </p>
        <Form.Form loading={formik.isSubmitting} onSubmit={formik.handleSubmit}>
          <Dropdown
            label="Tipo de Documento"
            options={DOC_TYPES}
            selected={formik.values.selectedTypeDoc}
            onChange={handleSelect}
          />
          <div className="mt-5">
            <InputField
              autoComplete="off"
              behavior="currency"
              error={
                validateFieldError(
                  formik.touched.docNumber,
                  formik.errors.docNumber,
                  formik.values.docNumber.length
                ) || formik.errors.errorMsg
              }
              label="N° de Documento"
              maskConfig={{
                thousandSeparator: '.',
              }}
              maxLength={11} // Son 9 caracteres, pero la mascara de currency utiliza puntos para separar los miles, ejemplo XX.XXX.XXX
              minLength={2}
              name="docNumber"
              placeholder="Ingresar"
              success={formik.isValid && formik.values.docNumber.length > 0}
              value={formik.values.docNumber}
              onBlur={formik.handleBlur}
              onChange={(name, value) => {
                formik.setFieldValue(name, value);
              }}
            />
          </div>
          <div className="flex items-center justify-between lg:mt-[2.8rem]">
            <Button
              className="hidden lg:inline-block lg:flex-1 text-center no-underline font-bold"
              href={ROUTES.basePath}
              id="btnGoHome"
              type="link"
              variant="text"
            >
              Volver
            </Button>
            <Button
              className="w-full mt-8 lg:mt-0 lg:flex-1 lg:px-0"
              disabled={
                isLoading ||
                !formik.isValid ||
                formik.values.docNumber.length <= 0
              }
              id="btnPostDoc"
              type="submit"
              variant="primary"
            >
              Consultar
            </Button>
          </div>
        </Form.Form>
      </div>
    ),
    [formik, handleSelect, isLoading]
  );

  return (
    <>
      <Loader isVisible={isLoading && !isSuccess} timeout={true} />
      <AnimatePresenceComponent isLoaded={!isLoading}>
        {isMobile ? (
          mainContent()
        ) : (
          <Card title="Recordar e-mail">{mainContent()}</Card>
        )}
      </AnimatePresenceComponent>
    </>
  );
};
